/**
 * Booking flow state types
 */

import type { ProcessedSlot, TermAndCondition } from '../types/timeSlots';
import type { SelectedPairing } from '../types/pairings';
import type { SpecialEventGroup } from '../types/specialEvent';

/**
 * An alternative option returned by the waiting-list alternatives API.
 */
export interface WlAlternativeOption {
  date: string;
  serviceType: string;
}

/**
 * Service types available for booking
 */
export type ServiceType = 'breakfast' | 'lunch' | 'dinner' | 'brunch';

/**
 * Experience selection for special dining experiences
 */
export interface SelectedExperience {
  id: string;
  title: string;
}

/**
 * Area selection for seating preference
 */
export interface SelectedArea {
  id: string;
  name: string;
}

/**
 * Guarantee types for payment
 */
export type GuaranteeType = 'traditional' | 'deposit' | 'no_show';

/** Guarantee types that require a payment guarantee (card on file) */
export const PAYMENT_GUARANTEE_TYPES: readonly string[] = ['deposit', 'no_show'] as const;

/** Check if a type string represents a payment guarantee */
export function isPaymentGuaranteeType(type: string | null | undefined): boolean {
  return !!type && PAYMENT_GUARANTEE_TYPES.includes(type);
}

/**
 * Payment processor type
 */
export type PaymentProcessor = 'stripe' | 'mercadopago';

export type StripeTokenAccount = 'restaurant' | 'platform';

/**
 * Complete booking state
 */
export interface BookingState {
  /** Type of booking flow (standard, waiting list, etc.) */
  flowType: 'standard' | 'waiting_list';
  /** Whether this is an early access (request) flow */
  isEarlyAccessFlow: boolean;
  /**
   * Per-tenant flag controlling whether the Confirm step is skipped (commit
   * moves to the Guarantee/Personalize step). Read once from
   * `restaurant.bookingSkipConfirmationV2` at session start and frozen for
   * the lifetime of this session — mid-session changes to the restaurant
   * config DO NOT propagate (A2 decision in
   * `docs/plans/remove-confirm-step.md`).
   *
   * Defaults to `false` when the restaurant payload omits the field or sets
   * it to a non-boolean — legacy/Confirm-step behavior preserved.
   *
   * No setter is exposed: this is a derived-at-mount value, not an action.
   */
  skipConfirmStep: boolean;
  /** Whether the selected date is a special day (event, holiday, etc.) */
  isSelectedDateSpecial: boolean;
  /** Number of diners */
  diners: number;
  /** Selected date in yyyy-MM-dd format (normalized by BookingProvider). Use new Date(selectedDate + 'T12:00:00') when a Date object is needed. */
  selectedDate: string | null;
  /** Selected service type */
  service: ServiceType | null;
  /** Selected time slot */
  selectedTime: string | null;
  /** Available time slots for selected service */
  timeSlots: ProcessedSlot[] | null;
  /** Selected experience (optional) */
  selectedExperience: SelectedExperience | null;
  /** Selected area (optional) */
  selectedArea: SelectedArea | null;
  /** Whether the experience flow is active */
  showExperience: boolean;
  /** Slot-level terms and conditions from API */
  slotTerms: TermAndCondition[];
  /** Special requests or notes */
  specialRequests: string;
  /** Timestamp when booking was started */
  startedAt: number;

  // Personalization
  allergies: string;
  /**
   * True iff the user has explicitly answered the "dietary restrictions?"
   * Yes/No toggle on Personalize (either to Yes-with-text or to No). Decouples
   * "user said No" from "user hasn't answered" — both used to map to
   * `allergies === ''` and made the No selection lost on back-nav return.
   * Set true from `handleContinue` alongside the allergies update.
   */
  hasAllergiesAnswered: boolean;
  veggie: boolean;
  howManyVeggie: number;
  kids: boolean;
  howManyKids: number;
  pets: boolean;
  accessibility: boolean;
  acceptedTerms: boolean;

  // Guarantee/Payment
  guaranteeType: GuaranteeType;
  cardToken: string | null;
  feeDeposit: number;
  feeGratuity: number;
  paymentMethod: string | null;
  paymentProcessor: PaymentProcessor | null;
  stripeTokenAccount: StripeTokenAccount | null;
  cardTokenStripe: string | null;
  encryptedCardNumber: string | null;
  encryptedExpiry: string | null;
  encryptedCvc: string | null;
  encryptedHolderName: string | null;

  // Menu filter (Step 3C)
  selectedMenuFilter: number[] | null;
  // Area filter (Flow D — pre-time)
  areaFilterIds: number[] | null;
  // Beverage pairings (maridaje)
  selectedPairings: SelectedPairing[];
  // Column type from API (no-availability variant)
  columnType: string | null;
  // Calendar day type from calendar API (e.g. "waiting-list", "available", "walk-in")
  selectedCalendarDayType: string | null;
  // User nationality for special rules
  isForeign: boolean;
  // User-selected gratuity percentage
  tipChargePercent: number | null;
  // Gift card token
  giftCardToken: string | null;
  // Agent reservation flags
  isAgentReservation: boolean;
  agentRole: string | null;
  requestGuarantee: boolean;
  // AMEX Brasil routing
  amexChange: boolean;

  // Special event
  specialEvent: SpecialEventGroup | null;

  // Preferred access
  /** True when center column has ONLY preferred slots (no normal slots) */
  centerHasOnlyPreferred: boolean;

  // Billing
  /** Billing address (when allowFullBillingDetails) */
  billingAddress: string;
  /** Identity/passport document number (Peru/Argentina) */
  identityDocument: string;
  /** Peru billing type: 'boleta' (personal) or 'factura' (business) */
  billType: 'boleta' | 'factura' | null;
  /** Peru boleta fields */
  billPersonalName: string;
  billPersonalDocument: string;
  billPersonalAddress: string;
  /** Peru factura fields */
  billBusinessName: string;
  billBusinessDocument: string;
  billBusinessAddress: string;

  // Deep-link & Meitre Network
  /** Center hour from deep-link for auto-selection in SelectTimePage */
  deepLinkCenterHour: string | null;
  /** When true, the date was supplied by a deep link and the DATE step is skipped */
  deepLinkDateLocked: boolean;
  /** Marketplace menu ID from Meitre Network (/reserva?exp) */
  meitreNetworkMenuId: number | null;
  /** Menu ID pre-selected from landing page (uses calendar-marketplace for filtering, but reservation is normal) */
  preSelectedMenuId: number | null;
  /** Whether this is a staff/platform booking */
  isStaffBooking: boolean;

  // Reservation result
  reservationId: string | null;
  /** Public-facing reservation ID for display */
  uniqueId: string | null;
  /** Reschedule: ID of the reservation being rescheduled */
  rescheduleId: number | null;
  /** Reschedule: guarantee type of the original reservation */
  rescheduleOriginalType: string | null;
  /** Reschedule: service type to pre-select */
  rescheduleServiceType: string | null;
  /** Reschedule: menu ID to pre-select */
  rescheduleMenuId: number | null;

  // Guest info
  guestName: string;
  guestEmail: string;
  guestPhone: string;

  // Notes (post-confirmation)
  userNote: string;

  // Calendar intent
  /** Whether the user has already seen the calendar and selected a date.
   *  First visit shows all non-closed days as available (intent capture).
   *  After selecting a date and advancing, shows real availability. */
  calendarIntentCaptured: boolean;
  /** Service types that have real availability for the selected date (from calendar API) */
  availableServiceTypesForDate: ServiceType[];

  // Waiting list
  wlDates: string[];
  wlPreferredTime: string | null;
  wlMenus: number[];
  /** Menus extracted from raw center slots (includes unavailable slots) for WL form display */
  wlSlotMenus: { id: number; name: string; eventMenu: boolean; orderPriority: number; allowPairings?: boolean; isPairingMandatory?: boolean }[];
  wlServiceType: ServiceType | null;
  wlAlternatives: WlAlternativeOption[] | null;
  suggestedFromWaitingListAlternatives: boolean;
}

/**
 * Actions to update booking state
 */
export interface BookingActions {
  /** Set number of diners */
  setDiners: (diners: number) => void;
  /** Set selected date */
  setSelectedDate: (date: string | null) => void;
  /** Set service type */
  setService: (service: ServiceType | null) => void;
  /** Set selected time */
  setSelectedTime: (time: string | null) => void;
  /** Set available time slots */
  setTimeSlots: (slots: ProcessedSlot[] | null) => void;
  /** Set selected experience */
  setSelectedExperience: (experience: SelectedExperience | null) => void;
  /** Set selected area */
  setSelectedArea: (area: SelectedArea | null) => void;
  /** Toggle experience flow visibility */
  setShowExperience: (show: boolean) => void;
  /** Set special requests */
  setSpecialRequests: (requests: string) => void;
  /** Set slot-level terms and conditions */
  setSlotTerms: (terms: TermAndCondition[]) => void;
  /** Set personalization fields */
  setAllergies: (allergies: string) => void;
  setVeggie: (veggie: boolean) => void;
  setHowManyVeggie: (count: number) => void;
  setKids: (kids: boolean) => void;
  setHowManyKids: (count: number) => void;
  setPets: (pets: boolean) => void;
  setAccessibility: (accessibility: boolean) => void;
  setAcceptedTerms: (accepted: boolean) => void;
  /** Set guarantee/payment fields */
  setGuaranteeType: (type: GuaranteeType) => void;
  setCardToken: (token: string | null) => void;
  setFeeDeposit: (fee: number) => void;
  setPaymentMethod: (method: string | null) => void;
  /** Set menu filter (Step 3C) */
  setSelectedMenuFilter: (filter: number[] | null) => void;
  /** Set area filter (Flow D — pre-time) */
  setAreaFilterIds: (filter: number[] | null) => void;
  /** Set selected beverage pairings */
  setSelectedPairings: (pairings: SelectedPairing[]) => void;
  /** Set column type from API */
  setColumnType: (type: string | null) => void;
  /** Set user nationality */
  setIsForeign: (isForeign: boolean) => void;
  /** Set gratuity percentage */
  setTipChargePercent: (percent: number | null) => void;
  /** Set reservation result */
  setReservationResult: (id: string, uniqueId?: string) => void;
  /** Set guest info */
  setGuestInfo: (name: string, email: string, phone: string) => void;
  /** Set user note */
  setUserNote: (note: string) => void;
  /** Set waiting list dates */
  setWlDates: (dates: string[]) => void;
  /** Set waiting list preferred time */
  setWlPreferredTime: (time: string | null) => void;
  /** Set waiting list menus */
  setWlMenus: (menus: number[]) => void;
  /** Set waiting list service type */
  setWlServiceType: (serviceType: ServiceType | null) => void;
  /** Reset entire booking state */
  resetBooking: () => void;
  /** Update multiple fields at once */
  updateBooking: (updates: Partial<BookingState>) => void;
}

/**
 * Combined booking context value
 */
export interface BookingContextValue extends BookingState, BookingActions {
  /** Whether state is being loaded from storage */
  isLoading: boolean;
  /** Whether booking has minimum required data */
  isValid: boolean;
}

/**
 * Default booking state
 */
export const DEFAULT_BOOKING_STATE: BookingState = {
  flowType: 'standard',
  isEarlyAccessFlow: false,
  // Overwritten on first mount by BookingProvider from the restaurant payload;
  // see the frozen-on-init contract in the field's TSDoc above.
  skipConfirmStep: false,
  isSelectedDateSpecial: false,
  diners: 2,
  selectedDate: null,
  service: null,
  selectedTime: null,
  timeSlots: null,
  selectedExperience: null,
  selectedArea: null,
  showExperience: false,
  slotTerms: [],
  specialRequests: '',
  startedAt: Date.now(),
  // Personalization
  allergies: '',
  hasAllergiesAnswered: false,
  veggie: false,
  howManyVeggie: 0,
  kids: false,
  howManyKids: 0,
  pets: false,
  accessibility: false,
  acceptedTerms: false,
  // Guarantee/Payment
  guaranteeType: 'traditional',
  cardToken: null,
  feeDeposit: 0,
  feeGratuity: 0,
  paymentMethod: null,
  paymentProcessor: null,
  stripeTokenAccount: null,
  cardTokenStripe: null,
  encryptedCardNumber: null,
  encryptedExpiry: null,
  encryptedCvc: null,
  encryptedHolderName: null,
  // Menu filter (Step 3C)
  selectedMenuFilter: null,
  // Area filter (Flow D — pre-time)
  areaFilterIds: null,
  // Beverage pairings
  selectedPairings: [],
  // Column type
  columnType: null,
  selectedCalendarDayType: null,
  // User nationality
  isForeign: false,
  // Gratuity
  tipChargePercent: null,
  // Gift card
  giftCardToken: null,
  // Special event
  specialEvent: null,
  // Preferred access
  centerHasOnlyPreferred: false,
  // Billing
  billingAddress: '',
  identityDocument: '',
  billType: null,
  billPersonalName: '',
  billPersonalDocument: '',
  billPersonalAddress: '',
  billBusinessName: '',
  billBusinessDocument: '',
  billBusinessAddress: '',
  // Deep-link & Meitre Network
  deepLinkCenterHour: null,
  deepLinkDateLocked: false,
  meitreNetworkMenuId: null,
  preSelectedMenuId: null,
  isStaffBooking: false,
  // Agent
  isAgentReservation: false,
  agentRole: null,
  requestGuarantee: false,
  amexChange: false,
  // Reservation result
  reservationId: null,
  uniqueId: null,
  rescheduleId: null,
  rescheduleOriginalType: null,
  rescheduleServiceType: null,
  rescheduleMenuId: null,
  // Guest info
  guestName: '',
  guestEmail: '',
  guestPhone: '',
  // Notes
  userNote: '',
  // Calendar intent
  calendarIntentCaptured: false,
  availableServiceTypesForDate: [],
  // Waiting list
  wlDates: [],
  wlPreferredTime: null,
  wlMenus: [],
  wlSlotMenus: [],
  wlServiceType: null,
  wlAlternatives: null,
  suggestedFromWaitingListAlternatives: false,
};
